import request from '@/utils/request'
import { useAppStore } from '@/stores/app'
import validationUtil from '@/utils/validationUtil'

interface Shop {
  shopId: number
  shopName: string

  chainId: number
  chainName?: string
  chainShopId: string | null

  shoppingCenterId: number
  shoppingCenterName: string
  shoppingCenterGroupId?: number
  shoppingCenterGroupName: string
  shoppingCenterShopId: string

  sections: Section[]

  creationDate: Date | string
  deactivationDate: Date | string | null

  isDisabled?: boolean
  canEditShop?: boolean

  groupsRelated?: { groupId: number }[]
  membersRelated?: { memberId: number }[]
}


interface ShopToFind {
  ShopName: string
  ShoppingCenterName: string
  ChainShopId?: string | number
  SectionId: string | number
}

interface Section {
  sectionId: number
  sectionName: string
  sectionExternalId?: string
  shoppingCenterGroupId?: number
  shoppingCenterGroupName?: string
  updateSucceed?: boolean
  relatedFailureParameter?: any
  failureReason?: string
  revenueValue?: number
}

interface Chain {
  chainId: number
  chainName: string
  chainExternalId?: string
  chainGroupId?: number
  chainGroupName?: string
  shopsRelated?: { shopId: number }[]
  groupsRelated?: any[]
}

interface ShoppingCenter {
  shoppingCenterId: number
  shoppingCenterName: string
  shoppingCenterGroupName: string
  shoppingCenterGroupId?: number
}

interface OverrideInfo {
  length: number
  revenues: {
    shopId: number
    sectionId: number
    revenueValue?: number
  }[]
}

interface AllowedShopsListResult {
  success: boolean
  data: Shop[]
  error?: 'request_failed'
}

const shopsService = {
  async getAllowedShopsListResult(month?: string, year?: string): Promise<AllowedShopsListResult> {
    const path = '/shops/GetShops'
    const result = await request.get<Shop[]>(
      import.meta.env.VITE_REVENUE_API_URL + path,
      month ? { month, year } : undefined
    )

    if (!result) {
      return {
        success: false,
        data: [],
        error: 'request_failed'
      }
    }

    return {
      success: true,
      data: result
    }
  },

  async getAllowedShopsList(month?: string, year?: string): Promise<Shop[]> {
    const store = useAppStore()

    const result = await shopsService.getAllowedShopsListResult(month, year)
    if (result.success) {
      store.setAllowedShopsList(result.data)
    }
    return result.data
  },

  async postShop(body: Partial<Shop>): Promise<any> {
    const path = '/shops/UpdateShop'
    return await request.post(import.meta.env.VITE_REVENUE_API_URL + path, body)
  },

  async getChains(): Promise<Chain[]> {
    const path = '/shops/GetChains'
    const store = useAppStore()

    const result = await request.get<Chain[]>(import.meta.env.VITE_REVENUE_API_URL + path)
    
    if (result) {
      store.setChains(result)
    }
    return result || []
  },

  async getSections(): Promise<Section[]> {
    const path = '/shops/GetSections'
    const store = useAppStore()

    const result = await request.get<Section[]>(import.meta.env.VITE_REVENUE_API_URL + path)

    if (result) {
      store.setSections(result)
    }
    return result || []
  },

  getShopFromCollection(shopsCollection: Shop[], shopToFind: ShopToFind): Shop | undefined {
    return shopsCollection.find((shopItem) => 
      shopItem.shopName === shopToFind.ShopName &&
      shopItem.shoppingCenterName === shopToFind.ShoppingCenterName &&
      (shopItem.chainShopId === '' || 
       shopItem.chainShopId === null || 
       shopItem.chainShopId.toString() === shopToFind.ChainShopId?.toString()) &&
      shopItem.sections.find((section) => 
        section.sectionId.toString() === shopToFind.SectionId.toString()
      )
    )
  },

  async setShopActivation(shopId: number, isActive: boolean): Promise<boolean> {
    const path = '/shops/SetShopActivation'
    const body = { shopId, isActive }
    
    try {
      await request.post(import.meta.env.VITE_REVENUE_API_URL + path, body)
      return true
    } catch {
      return false
    }
  },

  addDetailsToShopSection(
    shopList: Shop[], 
    shopSection: Array<{
      shopId: number
      sectionId: number
      updateSucceed: boolean
      relatedFailureParameter: any
      failureReason: string
    }>, 
    overrideInfo: OverrideInfo
  ): Shop[] {
    shopList.forEach((shop) => {
      shop.sections.forEach((section) => {
        const shopDetails = shopSection.find(
          (sd) => sd.shopId === shop.shopId && sd.sectionId === section.sectionId
        )

        if (shopDetails) {
          section.updateSucceed = shopDetails.updateSucceed
          section.relatedFailureParameter = shopDetails.relatedFailureParameter
          section.failureReason = shopDetails.failureReason
        }
        
        if (overrideInfo.length !== 0) {
          const extraDetails = overrideInfo.revenues.find(
            (sd) => sd.shopId === shop.shopId && sd.sectionId === section.sectionId
          )
          section.revenueValue = extraDetails?.revenueValue
        }
      })
    })
    return shopList
  },

  validateUpdateShop(shop: Partial<Shop>, fields: any[]): string[] {
    const errors: string[] = []
    const fieldsValidationResult = validationUtil.checkFieldValidity(fields)

    if (!fieldsValidationResult) {
      errors.push('FormInvalid')
    }
    if (shop.chainId === 0) {
      errors.push('MissingChainId')
    }
    if (shop.deactivationDate !== null && shop.creationDate && shop.deactivationDate && 
        new Date(shop.creationDate) > new Date(shop.deactivationDate)) {
      errors.push('CreationDateIsBiggerThanDeactivationDate')
    }
    if (!shop.sections || shop.sections.length === 0) {
      errors.push('MissingSection')
    }
    if (shop.shoppingCenterId === 0) {
      errors.push('MissingShoppingCenterId')
    }
    return errors
  },

  prepareShopsData(
    shopsList: Shop[], 
    groupList: Array<{ groupId: number; shopIds: number[] }>, 
    memberList: Array<{ memberId: number; groupId: number }>, 
    chainsList: Chain[]
  ): Shop[] {
    const distinctShopsList: Shop[] = []
    
    if (shopsList) {
      shopsList.forEach((element) => {
        const shopExists = distinctShopsList.find((g) => g.shopId === element.shopId)
        
        const relatedGroups = groupList?.filter(group => 
          group.shopIds.includes(element.shopId)
        )?.map(item => ({ groupId: item.groupId })) ?? []
        
        const relatedMembers = memberList?.filter(member => 
          relatedGroups.some(g => g.groupId === member.groupId)
        )?.map(item => ({ memberId: item.memberId })) ?? []
        
        const creationDate = element.creationDate 
          ? new Date(element.creationDate).toLocaleDateString()
          : ""
        
        const deactivationDate = element.deactivationDate 
          ? new Date(element.deactivationDate).toLocaleDateString()
          : "-"
        
        const chainRelated = chainsList?.find(chain => chain.chainId === element.chainId)

        if (shopExists === undefined) {
          distinctShopsList.push({
            shopId: element.shopId,
            shopName: element.shopName,
            chainId: element.chainId,
            chainName: chainRelated?.chainName,
            shoppingCenterId: element.shoppingCenterId,
            shoppingCenterGroupName: element.shoppingCenterGroupName,
            shoppingCenterGroupId: element.shoppingCenterGroupId,
            shoppingCenterName: element.shoppingCenterName,
            sections: element.sections,
            creationDate: creationDate,
            deactivationDate: deactivationDate,
            chainShopId: element.chainShopId,
            shoppingCenterShopId: element.shoppingCenterShopId,
            groupsRelated: relatedGroups,
            membersRelated: relatedMembers,
            canEditShop: element.canEditShop,
            isDisabled: element.isDisabled,
          })
        }
      })
    }
    return distinctShopsList
  },

  canCreateShops(shopList: Shop[]): boolean {
    return shopList?.some(shop => shop.canEditShop) ?? false
  },

  addNewShop(shopToUpdate: Shop): void {
    const store = useAppStore()
    store.addOrUpdateAllowedShopsList(shopToUpdate)
  },

  setShoppingCenterGroupsList(): void {
    const store = useAppStore()
    const shopsList = store.getAllowedShopsList
    
    if (!shopsList) return

    const filteredShoppingCenterInfo = [
      ...new Map(
        shopsList.map((item): ShoppingCenter => ({
          shoppingCenterId: item.shoppingCenterId as number,
          shoppingCenterName: item.shoppingCenterName as string,
          shoppingCenterGroupName: item.shoppingCenterGroupName as string,
        })).map((item) => [item.shoppingCenterName, item])
      ).values(),
    ]
    
    store.setShoppingCenters(filteredShoppingCenterInfo)
  },

  prepareChainDetails(
    chainsList: Chain[], 
    shopList: Shop[], 
    groupList: Array<{ groupId: number; shopIds: number[] }>
  ): Chain[] {
    const distinctChainsList: Chain[] = []
    
    if (chainsList) {
      chainsList.forEach((element) => {
        const chainExists = distinctChainsList.find((g) => g.chainId === element.chainId)
        
        const relatedShops = shopList?.filter(i => i.chainId === element.chainId)
          ?.map(item => ({ shopId: item.shopId })) ?? []
        
        const relatedGroups = groupList?.filter(group => 
          group.shopIds.some(shop => relatedShops.some(i => i.shopId === shop))
        ) ?? []

        if (chainExists === undefined) {
          distinctChainsList.push({
            chainId: element.chainId,
            chainName: element.chainName,
            chainExternalId: element.chainExternalId,
            chainGroupId: element.chainGroupId,
            chainGroupName: element.chainGroupName,
            shopsRelated: relatedShops,
            groupsRelated: relatedGroups,
          })
        }
      })
    }
    return distinctChainsList
  },

  validateChainData(fields: any[]): string[] {
    const errors: string[] = []
    const checkFieldValidity = validationUtil.checkFieldValidity(fields)

    if (!checkFieldValidity) {
      errors.push('FormInvalid')
    }
    return errors
  },

  async postChain(body: Partial<Chain>): Promise<any> {
    const path = '/shops/UpdateChain'
    return await request.post(import.meta.env.VITE_REVENUE_API_URL + path, body)
  },

  createChain(chain: Chain): void {
    const store = useAppStore()
    store.extendChainsList(chain)
  },

  updateChain(chain: Chain): void {
    const store = useAppStore()
    store.updateChainInList(chain)
  },

  validateSectionData(fields: any[]): string[] {
    const errors: string[] = []
    const checkFieldValidity = validationUtil.checkFieldValidity(fields)
    
    if (!checkFieldValidity) {
      errors.push('FormInvalid')
    }
    return errors
  },

  async postSection(body: Partial<Section>): Promise<any> {
    const path = '/shops/UpdateSection'
    return await request.post(import.meta.env.VITE_REVENUE_API_URL + path, body)
  },

  async handleSectionUpdate(sectionData: Partial<Section>, isNew: boolean): Promise<any> {
    const result = await this.postSection(sectionData)
    
    // Check if result exists (request.post throws on error)
    if (result !== undefined) {
      if (isNew) {
        if (typeof result !== 'number' || !Number.isFinite(result) || result <= 0) {
          throw new Error('Missing section id from UpdateSection response')
        }
        
        const newSectionId = result
        
        const sectionForStore: Section = {
          sectionId: newSectionId || 0,
          sectionName: sectionData.sectionName || '',
          sectionExternalId: sectionData.sectionExternalId || '',
          shoppingCenterGroupId: sectionData.shoppingCenterGroupId
        }
        
        // Update the ID in the input object so the component gets the new ID
        if (sectionData.sectionId === 0) {
            sectionData.sectionId = newSectionId
        }
        
        this.createSection(sectionForStore)
      } else {
        // For existing sections, update in store
        const sectionForStore: Section = {
          sectionId: Number(sectionData.sectionId),
          sectionName: sectionData.sectionName || '',
          sectionExternalId: sectionData.sectionExternalId || '',
          shoppingCenterGroupId: Number(sectionData.shoppingCenterGroupId)
        }
        this.updateSection(sectionForStore)
      }
    }
    
    return result
  },

  createSection(section: Section): void {
    const store = useAppStore()
    store.extendSectionsList(section)
  },

  updateSection(section: Section): void {
    const store = useAppStore()
    store.updateSectionInList(section)
  },

  prepareSectionDetails(sectionsList: Section[], shoppingCentersList: ShoppingCenter[]): Section[] {
    const distinctSectionsList: Section[] = []
    
    if (sectionsList) {
      sectionsList.forEach((section) => {
        const sectionExists = distinctSectionsList.find((g) => g.sectionId == section.sectionId)
        
        const match = shoppingCentersList?.find(shoppingCenter => 
          shoppingCenter.shoppingCenterGroupId == section.shoppingCenterGroupId
        )

        if (sectionExists === undefined) {
          distinctSectionsList.push({
            sectionId: section.sectionId,
            sectionName: section.sectionName,
            sectionExternalId: section.sectionExternalId,
            shoppingCenterGroupId: section.shoppingCenterGroupId,
            shoppingCenterGroupName: match?.shoppingCenterGroupName,
          })
        }
      })
    }
    return distinctSectionsList
  },
}

export default shopsService
